import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X, ArrowUpRight } from "lucide-react";
import { searchAll, formatINR } from "../data/products";

const SUGGESTIONS = ["Lavender", "Vanilla", "Oud", "Jewellery", "Fashion"];

export const SearchOverlay = ({ open, onClose }) => {
  const [query, setQuery] = useState("");

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  const { products, collections } = searchAll(query);
  const hasResults = products.length > 0 || collections.length > 0;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35 }}
          className="fixed inset-0 z-[80] overflow-y-auto bg-ivory/[0.98] backdrop-blur-sm"
          data-testid="search-overlay"
        >
          <button
            data-testid="search-close-button"
            onClick={onClose}
            aria-label="Close search"
            className="absolute right-6 top-6 text-ink transition-colors hover:text-gold md:right-10 md:top-8"
          >
            <X size={26} strokeWidth={1.2} />
          </button>
          <div className="mx-auto max-w-3xl px-6 pb-24 pt-28 md:pt-36">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}>
              <p className="text-[11px] uppercase tracking-[0.35em] text-gold">Search Zelvoria</p>
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Candles, fragrances, collections…"
                data-testid="search-input"
                className="mt-4 w-full border-b border-cocoa/25 bg-transparent py-4 font-serif text-3xl font-light text-ink outline-none transition-colors placeholder:text-taupe/40 focus:border-gold md:text-4xl"
              />
            </motion.div>

            {!query && (
              <div className="mt-10" data-testid="search-suggestions">
                <p className="text-[10px] uppercase tracking-[0.25em] text-taupe">Popular Searches</p>
                <div className="mt-4 flex flex-wrap gap-3">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      onClick={() => setQuery(s)}
                      data-testid={`search-suggestion-${s.toLowerCase()}`}
                      className="border border-cocoa/20 px-5 py-2.5 text-[11px] uppercase tracking-[0.2em] text-ink transition-colors hover:border-gold hover:text-gold"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {query && (
              <div className="mt-12" data-testid="search-results">
                {collections.length > 0 && (
                  <div className="mb-10">
                    <p className="text-[10px] uppercase tracking-[0.25em] text-taupe">Collections</p>
                    {collections.map((c) => (
                      <Link
                        key={c.slug}
                        to={c.path}
                        onClick={onClose}
                        data-testid={`search-collection-${c.slug}`}
                        className="group flex items-center justify-between border-b border-beige/70 py-4"
                      >
                        <span className="font-serif text-2xl text-ink transition-colors group-hover:text-gold">{c.name}</span>
                        <span className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-taupe">
                          {c.status === "coming-soon" ? "Coming Soon" : "Shop Now"}
                          <ArrowUpRight size={14} className="text-gold" />
                        </span>
                      </Link>
                    ))}
                  </div>
                )}
                {products.length > 0 && (
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.25em] text-taupe">Candles</p>
                    {products.map((p) => (
                      <Link
                        key={p.slug}
                        to={`/product/${p.slug}`}
                        onClick={onClose}
                        data-testid={`search-result-${p.slug}`}
                        className="group flex items-center gap-5 border-b border-beige/70 py-4"
                      >
                        <span className="h-16 w-14 shrink-0 overflow-hidden bg-cream">
                          <img src={p.image} alt={p.name} className="h-full w-full object-cover" />
                        </span>
                        <span>
                          <span className="block font-serif text-xl text-ink transition-colors group-hover:text-gold">{p.name}</span>
                          <span className="mt-1 block text-[10px] uppercase tracking-[0.2em] text-taupe">
                            {p.fragrance} · {p.category}
                          </span>
                        </span>
                        <span className="ml-auto text-sm font-medium text-ink">{formatINR(p.price)}</span>
                      </Link>
                    ))}
                  </div>
                )}
                {!hasResults && (
                  <p className="py-10 text-center font-serif text-2xl italic text-taupe" data-testid="search-no-results">
                    Nothing found for “{query}” — try a fragrance or collection.
                  </p>
                )}
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
